import { booleanFields } from "@/data/forms/booleanFields";
import { isISODate, isUSDate, convertISOToUSDate, convertUSToISODate } from "../dates";
import { getSlugByPdfId } from "./formUtils";
import { getLanguageIndex } from "../language";
import { booleanToString } from "..";
import { Addon } from "@/templates/types";

//TODO -> Migrar aquí todo lo necesario para poder leer los datos de api correctamente
//        al recuperar la info de data, y utilizar esta función en lugar de formatResponseDatesFromApi
//        ubicada en src/utils/Form/formUtils.ts
export function buildFormToLoad(obj: Record<string, any>): Record<string, any> {
  const formattedObj: Record<string, any> = {};

  for (const key in obj) {
    // Skip keys we don't need to reverse
    if (["documentType", "language", "pdfId", "addons"].includes(key)) {
      continue;
    }

    const value = obj[key];

    // Handle US dates -> convert to ISO and split into day/month/year
    if (isUSDate(value)) {
      const isoDate = convertUSToISODate(value);
      formattedObj[key] = isoDate;

      const [year, month, day] = isoDate.split("-"); // YYYY-MM-DD
      formattedObj[`${key}-day`] = day;
      formattedObj[`${key}-month`] = month;
      formattedObj[`${key}-year`] = year;
      continue;
    }

    // Convert string boolean to actual boolean when needed
    if (booleanFields.includes(key)) {
      formattedObj[key] = value === "1";
      continue;
    }

    switch (key) {
      case "passportStatus":
        formattedObj.passportBookLostType = value;
        break;

      case "passportCardStatus":
        formattedObj.passportCardLostType = value;
        break;

      case "birthCountry":
        formattedObj[key] = value;
        formattedObj.birthPlace = obj.birthPlace; // Re-attach birthPlace if needed
        break;

      default:
        formattedObj[key] = value;
        break;
    }
  }

  return formattedObj;
}

export function buildFormToSubmit(
  form: Record<string, any>,
  siteAddons?: Addon[] | null
): Record<string, any> {
  let formattedForm: Record<string, any> = { ...form };
  formattedForm.addons = getAddonsWithDiscount(form, siteAddons);

  for (const key in form) {
    formattedForm = { ...formattedForm, ...getValueToSubmit(key, formattedForm) };
  }

  return {
    ...formattedForm,
    documentType: getDocumentType(formattedForm),
    language: getLanguageIndex(form.language),
    pdfId: form.pdfId,
  };
}

function getValueToSubmit(key: string, form: Record<string, any>): any {
  const value = form[key];

  if (isISODate(value)) {
    return { [key]: convertISOToUSDate(value) };
  }

  if (typeof value === "boolean") {
    return { [key]: booleanToString(value) };
  }

  if (typeof value === "number") {
    return { [key]: value.toString() };
  }

  switch (key) {
    case "birthCountry":
      return {
        [key]: value,
        birthPlace: buildBirthPlace(form),
      };

    case "addons":
      return {
        [key]: getSelectedAddonsArrayId(form),
      };
    case "passportBookLostType":
      return {
        passportStatus: value,
        [key]: value,
      };
    case "passportCardLostType":
      return {
        passportCardStatus: value,
        [key]: value,
      };
    default:
      return { [key]: value };
  }
}

function buildBirthPlace(form: Record<string, any>): string {
  return `${form.birthCity} ${form.birthCountry === "US" ? form.birthState : form.birthCountry}`;
}

export function getDocumentType(form: Record<string, any>): string {
  const formAddons = Array.isArray(form.addons) ? form.addons : [];
  const addonCardItem = formAddons.filter((addon: any) => addon.identifier === "card");
  if (addonCardItem.length) {
    const cardIsSelected = addonCardItem[0].value === "1";
    if (cardIsSelected) return "both";
  }
  return "passportbook";
}

function getSelectedAddonsArrayId(form: Record<string, any>): string[] {
  const formAddons = Array.isArray(form.addons) ? form.addons : [];
  const filteredAddons = formAddons.filter(({ value }: { value: string }) => value === "1");
  return filteredAddons.map((addon: any) => addon.id);
}

export function getAddonsWithDiscount(
  form: Record<string, any>,
  siteAddons?: Addon[] | null
): any[] {
  const formAddons = Array.isArray(form.addons) ? form.addons : [];
  let discountAddon = formAddons.filter((item: any) => item.identifier === "discount")[0];
  const nonDiscountAddons = formAddons.filter((item: any) => item.identifier !== "discount");

  const slug = getSlugByPdfId(form.pdfId);
  // The discount addon only applies to the DS-64 (lost-stolen-passport) flow when the
  // user is also submitting a new passport AND the site-info has the addon checked.
  // It never applies to any other form (PASGF-2744).
  const siteDiscountChecked =
    siteAddons?.some(
      (siteAddon) => siteAddon.identifier === "discount" && siteAddon.checked === true
    ) ?? false;
  const discountEligible =
    slug === "lost-stolen-passport" &&
    form["submittingNewPassport"] === "yes" &&
    siteDiscountChecked;

  if (discountAddon) {
    discountAddon = { ...discountAddon, value: discountEligible ? "1" : "0" };
  }

  return [...nonDiscountAddons, ...(discountAddon ? [discountAddon] : [])];
}
